/**
 * Staff edit form model (BON-398) — the pure part of "what changed?".
 *
 * Extracted from StaffEditPage, which carried this inline and had drifted well
 * past the lint size and complexity caps. Pulling the diff out makes it
 * directly testable, which matters because it decides what gets written: the
 * bridges are REPLACE-IN-FULL, so a wrong answer here doesn't write a wrong
 * value, it wipes a set.
 */
import type { StaffMemberDetail, StaffPatch } from '../../api/staff';

/** Scalar fields the form can edit. Bridges are handled separately below. */
export const EDITABLE_KEYS: Array<keyof StaffPatch> = [
  'firstName',
  'lastName',
  'email',
  'displayName',
  'fareharborUsername',
  'phone',
  'hireDate',
  'bio',
  'notes',
];

export function formatHireDate(v: unknown): string {
  if (!v) return '';
  if (typeof v === 'string') return v.slice(0, 10);
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  return '';
}

/** Order-insensitive set equality for the bridge fields. */
export function sameSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const x = [...a].sort();
  const y = [...b].sort();
  return x.every((v, i) => v === y[i]);
}

/**
 * The minimal patch for this form state — only fields the user actually
 * changed.
 *
 * Both bridges are omitted unless the resulting SET differs from what the
 * member already has. That matters more than it looks: sending `regionIds` or
 * `qualifiedPositions` unchanged is not a no-op write, it's a delete-then-
 * reinsert that churns audit history and, for qualifications, resets
 * `grantedBy`.
 */
export function buildStaffPatch(member: StaffMemberDetail, form: StaffPatch): StaffPatch {
  const patch: StaffPatch = {};
  const memberRecord = member as unknown as Record<string, unknown>;

  EDITABLE_KEYS.forEach((key) => {
    const next = form[key];
    if (next === undefined) return;
    const current = key === 'hireDate' ? formatHireDate(memberRecord[key]) : memberRecord[key];
    const normalizedNext = next === '' ? null : next;
    if (normalizedNext === current) return;
    (patch as Record<string, unknown>)[key as string] = normalizedNext;
  });

  if (form.regionIds !== undefined) {
    const current = (member.regions ?? []).map((r) => r.id);
    if (!sameSet(form.regionIds, current)) patch.regionIds = form.regionIds;
  }

  // Crew qualifications (BON-398). Read and write now share the name
  // `qualifiedPositions` (BON-419), so this diffs the field against itself.
  // The member's separate `positions` rate stub is unrelated.
  if (form.qualifiedPositions !== undefined) {
    const current = member.qualifiedPositions ?? [];
    if (!sameSet(form.qualifiedPositions, current)) {
      patch.qualifiedPositions = form.qualifiedPositions;
    }
  }

  return patch;
}
